document.addEventListener("DOMContentLoaded", () => {
    const selectors = {
        section: ".section-animate-modules",
        row: ".et_pb_row",
        column: ".et_pb_column",
        module: ".et_pb_module",
        visualBuilder: "#et-fb-app, .et-fb-app, .et-fb-root-ancestor",
    };

    const timing = {
        moduleDelayStep: 120,
        columnDelayStep: 200,
        rowRevealGap: 80,
        rowQueueStep: 180,
        revealStartRatio: 0.88,
        revealTimeout: 40,
        observerRootMargin: "0px 0px -12% 0px",
        layoutShiftChecks: [250, 800],
    };

    const isDiviVisualBuilder =
        document.body.classList.contains("et-fb") ||
        document.body.classList.contains("et-fb-root-ancestor") ||
        document.querySelector(selectors.visualBuilder) !== null;

    if (isDiviVisualBuilder) return;

    const sections = Array.from(document.querySelectorAll(selectors.section));

    if (!sections.length) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const useModuleReveal = window.matchMedia("(max-width: 979px)").matches;

    const revealRows = [];
    const revealModules = [];
    const rowModules = new Map();
    const rowRevealDurations = new Map();

    const getViewportHeight = () => window.innerHeight || document.documentElement.clientHeight;

    const isInRevealRange = (element) => {
        const rect = element.getBoundingClientRect();
        const viewportHeight = getViewportHeight();

        return rect.top < viewportHeight * timing.revealStartRatio && rect.bottom > viewportHeight * -0.1;
    };

    const getRowsForSection = (section) => {
        const rows = Array.from(section.querySelectorAll(selectors.row));

        return rows.length ? rows : [section];
    };

    const getColumnDelay = (row, module) => {
        const column = module.closest(selectors.column);

        if (!column || !row.contains(column)) return null;

        const rowColumns = Array.from(row.querySelectorAll(selectors.column));
        const columnModules = Array.from(column.querySelectorAll(selectors.module));
        const rowColumnIndex = rowColumns.indexOf(column);
        const moduleIndexInColumn = columnModules.indexOf(module);

        if (rowColumnIndex < 0 || moduleIndexInColumn < 0) return null;

        return rowColumnIndex * timing.columnDelayStep + moduleIndexInColumn * timing.moduleDelayStep;
    };

    const revealModule = (module) => {
        if (module.classList.contains("is-visible")) return;

        window.setTimeout(() => {
            module.classList.add("is-visible");

            module.addEventListener(
                "animationend",
                () => {
                    module.classList.add("is-revealed");
                },
                { once: true },
            );
        }, timing.revealTimeout);
    };

    const runLayoutShiftChecks = (callback) => {
        callback();
        timing.layoutShiftChecks.forEach((delay) => {
            window.setTimeout(callback, delay);
        });
    };

    const prepareRevealItems = () => {
        sections.forEach((section) => {
            getRowsForSection(section).forEach((row) => {
                const modules = Array.from(row.querySelectorAll(selectors.module));

                if (!modules.length) return;

                revealRows.push(row);
                rowModules.set(row, modules);
                section.classList.add("is-ready");

                let maxRevealDelay = 0;

                modules.forEach((module, index) => {
                    const revealDelay = useModuleReveal
                        ? 0
                        : (getColumnDelay(row, module) ?? index * timing.moduleDelayStep);

                    revealModules.push(module);
                    maxRevealDelay = Math.max(maxRevealDelay, revealDelay);
                    module.style.setProperty("--module-reveal-delay", `${revealDelay}ms`);

                    if (reduceMotion) {
                        module.classList.add("is-visible");
                        module.classList.add("is-revealed");
                    }
                });

                rowRevealDurations.set(row, maxRevealDelay + timing.rowRevealGap);
            });
        });
    };

    const startModuleReveal = () => {
        const revealVisibleModules = () => {
            revealModules.forEach((module) => {
                if (!module.classList.contains("is-visible") && isInRevealRange(module)) {
                    revealModule(module);
                }
            });
        };

        if (!("IntersectionObserver" in window)) {
            revealVisibleModules();

            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;

                    revealModule(entry.target);
                    observer.unobserve(entry.target);
                });
            },
            {
                root: null,
                rootMargin: timing.observerRootMargin,
                threshold: 0.01,
            },
        );

        revealModules.forEach((module) => {
            observer.observe(module);
        });

        runLayoutShiftChecks(revealVisibleModules);
    };

    const startRowReveal = () => {
        let activeRow = false;
        const queuedRows = new Set();

        const flushRowQueue = () => {
            if (activeRow || !queuedRows.size) return;

            const nextRow = revealRows.find((row) => queuedRows.has(row));

            if (!nextRow) return;

            const modules = rowModules.get(nextRow) || [];
            const rowDuration = rowRevealDurations.get(nextRow) || timing.rowRevealGap;
            const queueDuration = Math.min(rowDuration, timing.rowQueueStep);

            queuedRows.delete(nextRow);
            activeRow = true;
            modules.forEach(revealModule);

            window.setTimeout(() => {
                activeRow = false;
                flushRowQueue();
            }, queueDuration);
        };

        const queueRowReveal = (row, flushImmediately = true) => {
            if (!rowModules.has(row) || row.classList.contains("is-reveal-queued")) return;

            row.classList.add("is-reveal-queued");
            queuedRows.add(row);

            if (flushImmediately) {
                flushRowQueue();
            }
        };

        const revealVisibleRows = () => {
            revealRows.forEach((row) => {
                if (!row.classList.contains("is-reveal-queued") && isInRevealRange(row)) {
                    queueRowReveal(row);
                }
            });
        };

        if (!("IntersectionObserver" in window)) {
            revealVisibleRows();

            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => revealRows.indexOf(a.target) - revealRows.indexOf(b.target))
                    .forEach((entry) => {
                        queueRowReveal(entry.target, false);
                        observer.unobserve(entry.target);
                    });

                flushRowQueue();
            },
            {
                root: null,
                rootMargin: timing.observerRootMargin,
                threshold: 0.01,
            },
        );

        revealRows.forEach((row) => {
            observer.observe(row);
        });

        runLayoutShiftChecks(revealVisibleRows);
    };

    prepareRevealItems();

    if (reduceMotion || !revealModules.length) return;

    requestAnimationFrame(() => {
        requestAnimationFrame(useModuleReveal ? startModuleReveal : startRowReveal);
    });
});
